const RepaymentMethod = {
  Annuity: 'Annuity',
  FixedAnnuity: 'FixedAnnuity',
  Serial: 'Serial',
} as const;

export type RepaymentMethodFI = (typeof RepaymentMethod)[keyof typeof RepaymentMethod];

type GetPaymentInstallmentProps = {
  loanAmount: number;
  interestRate: number;
  repaymentMethod: RepaymentMethodFI;
  maturity: number;
};

export type PaymentPlanProps = {
  loanAmount: number;
  instalmentAmount: number;
  interestRate: number;
  interval?: number;
  paymentType?: string;
};

type Payment = {
  year: number;
  amount: number;
};

export type PaymentTableItem = {
  installmentCount: number;
  currentPayment: number;
  payment: number;
  interest: number;
  loanAmount: number;
};

export function getPaymentInstallment(
  loanAmount: number,
  interestRate: number,
  repaymentMethod: RepaymentMethodFI,
  maturity: number,
) {
  const loanPeriod = getMaturityMonths(maturity);

  if (interestRate === 0) {
    return loanAmount / loanPeriod;
  }

  if (repaymentMethod !== RepaymentMethod.Serial) {
    return calculatePaymentInstallment({
      interestRate,
      loanAmount,
      loanPeriod,
    });
  }

  return loanAmount / loanPeriod + ((interestRate / 100) * loanAmount) / 12;
}

export const getMaturityMonths = (maturity: number) => maturity * 12;

type CalculatePaymentInstallmentProps = {
  interestRate: number;
  loanAmount: number;
  loanPeriod: number;
};

function calculatePaymentInstallment({ interestRate, loanAmount, loanPeriod }: CalculatePaymentInstallmentProps) {
  const months = 1 + interestRate / (12 * 100);
  return ((Math.pow(months, loanPeriod) * (months - 1)) / (Math.pow(months, loanPeriod) - 1)) * loanAmount;
}

export const getFirstInterest = (loanAmount: number, interestRate: number) => (loanAmount * (interestRate / 100)) / 12;

/* These functions are based on some visual basic code we received from business excel file on how they calculate these values
  this is for us not to copy paste the whole matrix, but to have the ground truth calculations in the code. Original functions can be found below
 */
function calculateRate(age: number) {
  const k = -2;
  const adjustment = Math.max(0, age + k - 72);

  return 1.15 * (0.00048 + Math.pow(10, 0.055 * (age + k - 94.5)) * Math.pow(10, -0.02 * adjustment));
}

const getIspLevel = (isp: string) => Number(isp.slice(0, -1)) / 100;

function calculateLoanInsuranceCodebtors(age1: number, age2: number, hasDiscount = false) {
  const kappa = hasDiscount ? 0.2 : 0.3;
  const phi = 0.1;
  const epsilon = 0.001;

  if (15 < age1 && age1 <= 65 && 15 < age2 && age2 <= 65) {
    return (
      ((1 + phi) * calculateRate(age1 + 0.5) + epsilon / 2 + ((1 + phi) * calculateRate(age2 + 0.5) + epsilon / 2)) /
        ((1 - kappa) * 1.025) +
      0.00099 / (1 - kappa)
    );
  }

  return 0;
}

function calculateLoanInsuranceSingleDebtor(age: number, hasDiscount = false): number {
  const kappa = hasDiscount ? 0.2 : 0.3;
  const phi = 0.1;
  const epsilon = 0.001;

  if (age > 15 && age <= 65) {
    return ((1 + phi) * calculateRate(age + 0.5) + epsilon) / ((1 - kappa) * 1.025) + 0.00054 / (1 - kappa);
  }

  return 0;
}

export type GetLsiRateProps = {
  firstAge?: number | null;
  secondAge?: number | null;
  insuredPeople: number;
  loanSecurity: string;
};

function getLsiRate({ firstAge, secondAge, insuredPeople, loanSecurity }: GetLsiRateProps) {
  if (!firstAge || firstAge <= 0) {
    return 0;
  }

  if (secondAge && secondAge > 0 && insuredPeople === 2) {
    return getIspLevel(loanSecurity ?? '100%') * calculateLoanInsuranceCodebtors(firstAge, secondAge);
  }

  return getIspLevel(loanSecurity ?? '100%') * calculateLoanInsuranceSingleDebtor(firstAge);
}

type GetLsiProps = {
  loanAmount: number;
} & GetLsiRateProps;

export function getLsi(props: GetLsiProps) {
  const lsiRate = getLsiRate({ ...props });
  return props.loanAmount * (lsiRate / 12);
}

const monthlyInterest = (interest: number, interval = 12) => interest / 100 / interval;

// Calculate the payment plan for a loan's principal (Without interest)
export function calculatePaymentPlan({
  loanAmount,
  instalmentAmount,
  interestRate,
  interval = 12,
  paymentType = '',
}: PaymentPlanProps) {
  const interest = monthlyInterest(interestRate, interval);

  // Calculate payment strategy based on type
  const paymentCalculator =
    paymentType === RepaymentMethod.Serial
      ? createSerialPaymentCalculator(loanAmount, instalmentAmount, interest)
      : createAnnuityPaymentCalculator(instalmentAmount, interest);

  return calculateYearlyPayments({
    loanAmount,
    paymentCalculator,
    interval,
    maxMonths:
      paymentType === RepaymentMethod.Serial
        ? Math.ceil(loanAmount / (instalmentAmount - loanAmount * interest))
        : Infinity,
  });
}

// Generic yearly payment calculator
function calculateYearlyPayments({
  loanAmount,
  paymentCalculator,
  interval,
  maxMonths,
}: {
  loanAmount: number;
  paymentCalculator: (amountLeft: number) => number;
  interval: number;
  maxMonths: number;
}) {
  let amountLeft = loanAmount;
  const yearlyPayments: Payment[] = [];
  let yearlyAmount = 0;
  let month = 0;

  while (amountLeft > 0.1 && month < maxMonths) {
    month++;
    let payment = paymentCalculator(amountLeft);

    // final payment (don't overpay)
    if (amountLeft - payment < 0) {
      payment = amountLeft;
    }

    amountLeft -= payment;
    yearlyAmount += payment;

    // End of year or final payment
    if (month % interval === 0 || amountLeft <= 0.1) {
      yearlyPayments.push({
        year: Math.ceil(month / interval),
        amount: yearlyAmount,
      });
      yearlyAmount = 0;
    }
  }

  return yearlyPayments;
}

// Payment calculators
// Annuity
const createAnnuityPaymentCalculator = (instalmentAmount: number, interest: number) => (amountLeft: number) =>
  instalmentAmount - amountLeft * interest;

// Serial
const createSerialPaymentCalculator = (loanAmount: number, instalmentAmount: number, interest: number) => {
  const totalMonths = loanAmount / (instalmentAmount - loanAmount * interest);
  const principalPayment = loanAmount / totalMonths;
  return () => principalPayment;
};

export const getPaymentTable = ({
  loanAmount,
  interestRate,
  repaymentMethod,
  maturity,
}: GetPaymentInstallmentProps): PaymentTableItem[] => {
  const totalInstallment = getPaymentInstallment(loanAmount, interestRate, repaymentMethod, maturity);

  if (repaymentMethod === RepaymentMethod.Serial) {
    return calculateSerialPaymentPlan({
      loanAmount,
      totalInstallment: getMaturityMonths(maturity),
      interestRate,
    });
  }

  return calculatePaymentsPlan({ loanAmount, totalInstallment, interestRate });
};

type CalculatePaymentPlanProps = {
  loanAmount: number;
  totalInstallment: number;
  interestRate: number;
};

const calculatePaymentsPlan = ({ loanAmount, totalInstallment, interestRate }: CalculatePaymentPlanProps) => {
  const interest = monthlyInterest(interestRate);
  let amountLeft = loanAmount;
  const currentPayment = totalInstallment;
  const result: PaymentTableItem[] = [];
  let i = 0;

  do {
    i++;
    const interestAmount = amountLeft * interest;
    const payment = currentPayment - interestAmount;
    amountLeft = amountLeft - payment;
    result.push({
      installmentCount: i,
      currentPayment: amountLeft < 0 ? amountLeft + payment + interestAmount : currentPayment,
      payment: amountLeft < 0 ? amountLeft + payment : payment,
      interest: Math.round(interestAmount),
      loanAmount: Math.max(amountLeft, 0.1),
    });
  } while (amountLeft > 0.1);

  return result;
};

type calculateSerialPaymentPlanProps = {
  loanAmount: number;
  totalInstallment: number;
  interestRate: number;
};

const calculateSerialPaymentPlan = ({
  loanAmount,
  totalInstallment,
  interestRate,
}: calculateSerialPaymentPlanProps) => {
  const interest = monthlyInterest(interestRate);
  let amountLeft = loanAmount;
  const payment = loanAmount / totalInstallment;
  const result: PaymentTableItem[] = [];
  let i = 0;

  do {
    i++;
    const interestAmount = amountLeft * interest;
    amountLeft = amountLeft - payment;

    const currentPayment = payment + interestAmount;
    result.push({
      installmentCount: i,
      currentPayment: amountLeft < 0 ? amountLeft + payment + interestAmount : currentPayment,
      payment: amountLeft < 0 ? amountLeft + payment : payment,
      interest: Math.round(interestAmount),
      loanAmount: Math.max(amountLeft, 0.1),
    });
  } while (amountLeft > 0.1);

  return result;
};
